import React, {Component} from 'react';
import moment from 'moment';
import _ from 'lodash';
import ReactGA from 'react-ga';
import Promise from 'bluebird';

import C from 'util/Calculations';
import Api from 'util/Api';

import RadioToggle from 'components/RadioToggle';
import Loader from 'components/Loader';
import MiniLoader from 'components/MiniLoader';

import PatientChart from 'components/patient-chart/PatientChart';
import Card from 'components/Card';

import PatientDetails from './partial/PatientDetails';
import PatientSubNav from './partial/PatientSubNav';
import WeeklyChartCard from './cards/WeeklyChartCard';
import PatientAlerts from './partial/PatientAlerts';
import PatientInfoPanelRangeDisplayText from "components/PatientInfoPanelRangeDisplayText";
import {movementTotalTooltip, graphTooltip} from 'components/Tooltips/PatientChartTooltips';

import {WhisperWrapper} from "components/Tooltips/WhisperWrapper";
import RespiratoryChart from '../../../../../components/RespiratoryChart';
import PresenceChart from '../../../../../components/PresenceChart';
import ActivityChart from '../../../../../components/ActivityChart';
import ModularTrendsChart from '../../../../../components/ModularTrendsChart';
import ModularTrendsTimeSplitChart from '../../../../../components/ModularTrendsTimeSplitChart';
import ModularTrendsSpecificThreshholdChart from '../../../../../components/ModularTrendsSpecificThreshholdChart';
import ModularTrendsMovement from '../../../../../components/ModularTrendsChartMovement';
import ModularTrendsChartAlerts from '../../../../../components/ModularTrendsChartAlerts';

const MIN_TIMESTAMP = -864e13;
const PIXELS_TO_POINTS = 0.75;
const smallFontSize = 14;
const mediumFontSize = 18;
const largeFontSize = 22;

const dateTimePickerDefault = {
    startTime: '00:00',
    endTime: '23:59',
    startDate: moment().subtract(1, 'd'),
    endDate: moment(),
    lowerLimit: MIN_TIMESTAMP,
    upperLimit: moment(),
};

class RoomShow extends Component {
    constructor(props) {
        super(props);
        this._isMounted = false;
        this._pendingPromises = [];
        
        this.alertsOffCanvas = React.createRef(null);
        this.patientDataContainer = React.createRef(null);
        
        const range = this.getDefaultDateRange();

        this.state = {
            startTime: range.start,
            endTime: range.end,
            patient: 0,
            points: {},
            movements: [],
            respiration: [],
            outOfRangeStatus: [],
            sleepTransitions: [],
            visitTransitions: [],
            rangeTransitions: [],
            rangeOOOTransitions: [],
            smoothedSleep: [],
            smoothedVisits: [],
            smoothedRanges: [],
            visits: [],
            sleeping: [],
            isUsingCustomDateRange: false,
            smoothGraphData: true,
            patientGraphLoading: true,
            movementLoading: true,
            respirationLoading: true,
            visitLoading: true,
            alertLoading: true,
            rangeLoading: true,
            sleepLoading: false,
            wellnessReport: {},
            selectedTimeFrame: 'Last 25 Hours',
            timeFrameDropdownVisible: false,
            customRangePickerVisible: false,
            lowerLimit: MIN_TIMESTAMP,
            upperLimit: moment(),
            paramsOOR: {},
            paramsLR: {},
            paramsAvgResp: 0,
            paramsMOV: {},
            paramsALERT: {},
            SettingResp: {},
            dailyReport: null,
            weeklyReports: [],
            bedExits: [],
            thermalPresence: [],
        };

        this.handleApplyRange = this.handleApplyRange.bind(this);
        this.handleDatesChange = this.handleDatesChange.bind(this);
        this.handleClearDateRangePicker = this.handleClearDateRangePicker.bind(this);
        this.handleTimeFrameChange = this.handleTimeFrameChange.bind(this);
        this.analyzeVisitData = this.analyzeVisitData.bind(this);
        this.analyzeData = this.analyzeData.bind(this);
        this.analyzeRangeData = this.analyzeRangeData.bind(this);
        this.analyzeSleepData = this.analyzeSleepData.bind(this);
        this.loadRoomData = this.loadRoomData.bind(this);
        this.loadOldRoomData = this.loadOldRoomData.bind(this);
        this.addListeners = this.addListeners.bind(this);
        this.printDataReport = this.printDataReport.bind(this);
        this.loadPatientParamsOOR = this.loadPatientParamsOOR.bind(this);
        this.loadPatientParamsMOV = this.loadPatientParamsMOV.bind(this);
        this.loadPatientParamsLR = this.loadPatientParamsLR.bind(this);
        this.loadPatientPoints = this.loadPatientPoints.bind(this);
        this.loadPatientParamsAlert = this.loadPatientParamsAlert.bind(this);
    }

    getDefaultDateRange() {
        return {
            start: moment().subtract(29, 'hours'),
            end: moment()
        };
    }

    // Helper method to track and handle promises
    trackPromise(promise) {
        this._pendingPromises.push(promise);
        return promise.finally(() => {
            if (this._isMounted) {
                this._pendingPromises = this._pendingPromises.filter(p => p !== promise);
            }
        });
    }

    // Helper method for safe setState
    safeSetState(state, callback) {
        if (this._isMounted) {
            this.setState(state, callback);
        }
    }

    componentDidMount() {
        this._isMounted = true;
        
        // Initialize data loading
        this.loadRoomData().catch(error => {
            console.error('Failed to load initial data:', error);
        });

        // Force the app to reset every minute
        const refreshTimer = setInterval(() => {
            if (!this._isMounted) return;
            
            if (!this.state.isUsingCustomDateRange) {
                this.safeSetState(
                    {
                        endTime: moment(),
                    },
                    () => {
                        this.removeListeners();
                        this.addListeners();
                    }
                );
            } else {
                this.removeListeners();
                this.addListeners();
            }
        }, 60000);

        this.setState({ refreshTimer });
    }

    componentWillUnmount() {
        this._isMounted = false;
        
        // Cancel all pending promises
        this._pendingPromises.forEach(promise => {
            if (promise.cancel) {
                promise.cancel();
            }
        });
        
        // Clear the array
        this._pendingPromises = [];
        
        // Clear any intervals or timers
        if (this.state.refreshTimer) {
            clearInterval(this.state.refreshTimer);
        }
        
        // Remove listeners
        this.removeListeners();
    }

    loadRoomData() {
        const { auth, match } = this.props;
        const patientId = match.params.patientid;

        if (!patientId) {
            console.error('No patient ID provided');
            this.safeSetState({ 
                isLoading: false,
                error: 'No patient ID provided'
            });
            return Promise.reject(new Error('No patient ID provided'));
        }

        return this.trackPromise(
            Api.withAuth(auth)
                .getPatient({ id: patientId })
                .then((data) => {
                    if (!this._isMounted) return;

                    // Set initial patient data first
                    this.safeSetState({
                        patient: data,
                        isLoading: false
                    });

                    // Then load all the patient-specific data in parallel
                    return Promise.all([
                        this.loadPatientParamsAlert(patientId),
                        this.loadPatientParamsOOR(patientId),
                        this.loadPatientParamsMOV(patientId),
                        this.loadPatientParamsLR(patientId),
                        this.loadPatientParamsAvgResp(patientId),
                        this.loadPatientPoints(patientId),
                        this.loadPatientSettingResp(patientId),
                        this.loadReports(patientId)
                    ]).catch(error => {
                        console.error('Error loading patient data:', error);
                        if (this._isMounted) {
                            this.safeSetState({ 
                                error: 'Failed to load patient data'
                            });
                        }
                    });
                })
                .catch(error => {
                    console.error('Error loading room data:', error);
                    if (this._isMounted) {
                        this.safeSetState({ 
                            isLoading: false,
                            error: 'Failed to load room data'
                        });
                    }
                    return Promise.reject(error);
                })
        );
    }

    loadOldRoomData() {
        return Api.withAuth(this.props.auth)
            .getRoomData({
                ...this.props.match.params,
            })
            .then((data) =>
                Promise.fromCallback((callback) =>
                    this.setState(
                        {
                            ...this.state,
                            ...data,
                        },
                        callback
                    )
                )
            );
    }

    loadPatientParamsAlert(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();
        return Api.withAuth(this.props.auth)
            .getPatientParamsAlert({
                patient: patientId,
                startTime: startTime,
                endTime: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            ...this.state,
                            paramsALERT: data,
                            alertLoading: false,
                        },
                        callback
                    );
                })
            );
    }

    loadPatientParamsOOR(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();

        return Api.withAuth(this.props.auth)
            .getPatientParamsOOR({
                patient: patientId,
                startTime: startTime,
                endTime: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            ...this.state,
                            paramsOOR: data,
                            //isLoading: false
                        },
                        callback
                    );
                })
            );
    }

    loadPatientParamsMOV(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();

        return Api.withAuth(this.props.auth)
            .getPatientParamsMOV({
                patient: patientId,
                startTime: startTime,
                endTime: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState({movementLoading: false});
                    this.safeSetState(
                        {
                            ...this.state,
                            paramsMOV: data,
                        },
                        callback
                    );
                })
            );
    }

    loadPatientParamsLR(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();

        return Api.withAuth(this.props.auth)
            .getPatientParamsLR({
                patient: patientId,
                startTime: startTime,
                endTime: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            ...this.state,
                            paramsLR: data,
                        },
                        callback
                    );
                })
            );
    }

    loadPatientParamsAvgResp(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();

        return Api.withAuth(this.props.auth)
            .getPatientParamsAvgResp({
                patient: patientId,
                startTime: startTime,
                endTime: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            ...this.state,
                            paramsAvgResp: data.averageResp,
                        },
                        callback
                    );
                })
            );
    }

    loadPatientPoints(patientId) {
        const startTime = this.state.startTime.valueOf();
        const endTime = this.state.endTime.valueOf();

        return Api.withAuth(this.props.auth)
            .getPatientPoints({
                patient: patientId,
                start: startTime,
                end: endTime,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            movements: data.movements || [],
                            respiration: data.respiration || [],
                            visits: data.visits || [],
                            points: data,
                            outOfRangeStatus: data.range || [],
                            sleeping: data.sleeping || [],
                            patientGraphLoading: false
                        },
                        () => {
                            // Call analyzeData to handle all data processing
                            this.analyzeData();
                            console.log('Updated State:', {
                                movements: this.state.movements?.length,
                                respiration: this.state.respiration?.length,
                                sleepTransitions: this.state.sleepTransitions?.length,
                                visitTransitions: this.state.visitTransitions?.length,
                                rangeOOOTransitions: this.state.rangeOOOTransitions?.length
                            });
                            callback();
                        }
                    );
                })
            );
    }

    loadPatientSettingResp(patientId) {
        return Api.withAuth(this.props.auth)
            .getPatientSettingResp({
                patient: patientId,
            })
            .then((data) =>
                Promise.fromCallback((callback) => {
                    this.safeSetState(
                        {
                            SettingResp: data,
                        },
                        () => {
                            callback();
                        }
                    );
                })
            );
    }

    loadReports(patientId) {
        if (!this._isMounted) return Promise.resolve();

        const api = Api.withAuth(this.props.auth);
        
        return Promise.all([
            api.getLatestDailyReport(patientId),
            api.getWeekDailyReports(patientId),
            api.getLatestWellnessReport(patientId)
        ])
        .then(([dailyReport, weeklyReports, wellnessReport]) => {
            if (!this._isMounted) return;
                
            // Ensure data exists before setting state and extract data property if it exists
            const safeData = {
                dailyReport: (dailyReport && dailyReport.data) ? dailyReport.data : (dailyReport || null),
                weeklyReports: (weeklyReports && weeklyReports.data) ? 
                    weeklyReports.data.filter(report => report !== null) : // Filter out null reports
                    (weeklyReports || []),
                wellnessReport: (wellnessReport && wellnessReport.data) ? wellnessReport.data : (wellnessReport || null)
            };
            
            // Add additional debug logging to understand structure
            console.log('Raw API Response:', {
                dailyReport,
                weeklyReports,
                wellnessReport
            });

            // Detailed logging of report data
            console.log('Daily Report Details:', {
                exists: !!safeData.dailyReport,
                timestamp: safeData.dailyReport?.period_start,
                metrics: {
                    respiration: {
                        overall: safeData.dailyReport?.overall_resp,
                        overall_std_dev: safeData.dailyReport?.overall_resp_std_dev,
                        awake: safeData.dailyReport?.awake_resp,
                        day_sleep: safeData.dailyReport?.day_sleep_resp,
                        night_sleep: safeData.dailyReport?.night_sleep_resp,
                        status: safeData.dailyReport?.resp_status
                    },
                    sleep: {
                        total_average: safeData.dailyReport?.sleep_total_average,
                        day_average: safeData.dailyReport?.sleep_day_average,
                        night_average: safeData.dailyReport?.sleep_night_average
                    },
                    movement: {
                        score: safeData.dailyReport?.movement_score
                    },
                    presence: {
                        thermal: safeData.dailyReport?.thermal_presence,
                        secondary: safeData.dailyReport?.secondary_presence,
                        visit_alert_count: safeData.dailyReport?.visit_alert_count
                    },
                    bed_exits: {
                        count: safeData.dailyReport?.bed_exit_count,
                        alert_count: safeData.dailyReport?.bed_exit_alert_count
                    },
                    report_interval: safeData.dailyReport?.report_interval,
                    device_id: safeData.dailyReport?.device_id,
                    period_start: safeData.dailyReport?.period_start,
                    period_end: safeData.dailyReport?.period_end
                }
            });

            console.log('Weekly Reports Summary:', {
                count: safeData.weeklyReports.length,
                dateRange: safeData.weeklyReports.length ? {
                    start: safeData.weeklyReports[0]?.period_start,
                    end: safeData.weeklyReports[safeData.weeklyReports.length - 1]?.period_start
                } : null,
                metrics: safeData.weeklyReports.map(report => ({
                    period_start: report?.period_start,
                    period_end: report?.period_end,
                    overall_resp: report?.overall_resp,
                    awake_resp: report?.awake_resp,
                    day_sleep_resp: report?.day_sleep_resp,
                    night_sleep_resp: report?.night_sleep_resp,
                    sleep_total_average: report?.sleep_total_average,
                    sleep_day_average: report?.sleep_day_average,
                    sleep_night_average: report?.sleep_night_average,
                    overall_resp_std_dev: report?.overall_resp_std_dev,
                    bed_exits: {
                        count: report?.bed_exit_count,
                        alert_count: report?.bed_exit_alert_count
                    },
                    presence: {
                        thermal: report?.thermal_presence,
                        secondary: report?.secondary_presence,
                        visit_alert_count: report?.visit_alert_count
                    },
                    movement_score: report?.movement_score,
                }))
            });

            console.log('Wellness Report Analysis:', {
                exists: !!safeData.wellnessReport,
                metrics: {
                    average_resp: safeData.wellnessReport?.average_resp,
                    average_resp_std_dev: safeData.wellnessReport?.average_resp_std_dev,
                    average_resp_status: safeData.wellnessReport?.average_resp_status,
                    movement_average: safeData.wellnessReport?.movement_average,
                    movement_std_dev: safeData.wellnessReport?.movement_std_dev,
                    movement_status: safeData.wellnessReport?.movement_status,
                    sleep_average: safeData.wellnessReport?.sleep_average,
                    sleep_std_dev: safeData.wellnessReport?.sleep_std_dev,
                    sleep_status: safeData.wellnessReport?.sleep_status
                },
                thresholds: {
                    respiration: this.getThresholds("average_resp", safeData.wellnessReport),
                    movement: this.getThresholds("movement", safeData.wellnessReport),
                    sleep: this.getThresholds("total_sleep", safeData.wellnessReport)
                }
            });

            // Print wellness report data in detail
            console.log('\n=== WELLNESS REPORT DATA ===');
            if (safeData.wellnessReport) {
                console.log(JSON.stringify({
                    patient_id: safeData.wellnessReport.patient_id,
                    device_id: safeData.wellnessReport.device_id,
                    sleep_total: {
                        average: safeData.wellnessReport.sleep_total_average,
                        std_dev: safeData.wellnessReport.sleep_total_std_dev,
                        status: safeData.wellnessReport.sleep_total_status
                    },
                    sleep_day: {
                        average: safeData.wellnessReport.sleep_day_average,
                        std_dev: safeData.wellnessReport.sleep_day_std_dev,
                        status: safeData.wellnessReport.sleep_day_status
                    },
                    sleep_night: {
                        average: safeData.wellnessReport.sleep_night_average,
                        std_dev: safeData.wellnessReport.sleep_night_std_dev,
                        status: safeData.wellnessReport.sleep_night_status
                    },
                    awake_resp: {
                        average: safeData.wellnessReport.awake_resp_average,
                        std_dev: safeData.wellnessReport.awake_resp_std_dev,
                        status: safeData.wellnessReport.awake_resp_status
                    },
                    day_sleep_resp: {
                        average: safeData.wellnessReport.day_sleep_resp_average,
                        std_dev: safeData.wellnessReport.day_sleep_resp_std_dev,
                        status: safeData.wellnessReport.day_sleep_resp_status
                    },
                    night_sleep_resp: {
                        average: safeData.wellnessReport.night_sleep_resp_average,
                        std_dev: safeData.wellnessReport.night_sleep_resp_std_dev,
                        status: safeData.wellnessReport.night_sleep_resp_status
                    },
                    overall_resp: {
                        average: safeData.wellnessReport.overall_resp_average,
                        std_dev: safeData.wellnessReport.overall_resp_std_dev,
                        status: safeData.wellnessReport.overall_resp_status
                    },
                    overall_resp_std_dev: {
                        average: safeData.wellnessReport.overall_resp_std_dev_average,
                        std_dev: safeData.wellnessReport.overall_resp_std_dev_std_dev,
                        status: safeData.wellnessReport.overall_resp_std_dev_status
                    },
                    resp_alert: {
                        count: safeData.wellnessReport.resp_alert_count,
                        std_dev: safeData.wellnessReport.resp_alert_std_dev,
                        status: safeData.wellnessReport.resp_alert_status
                    },
                    bed_exit: {
                        count: safeData.wellnessReport.bed_exit_count,
                        std_dev: safeData.wellnessReport.bed_exit_std_dev,
                        status: safeData.wellnessReport.bed_exit_status
                    },
                    bed_exit_alert: {
                        count: safeData.wellnessReport.bed_exit_alert_count,
                        std_dev: safeData.wellnessReport.bed_exit_alert_std_dev,
                        status: safeData.wellnessReport.bed_exit_alert_status
                    },
                    can_prevent: {
                        count: safeData.wellnessReport.can_prevent_count,
                        std_dev: safeData.wellnessReport.can_prevent_std_dev,
                        status: safeData.wellnessReport.can_prevent_status
                    },
                    movement_score: {
                        score: safeData.wellnessReport.movement_score,
                        std_dev: safeData.wellnessReport.movement_score_std_dev,
                        status: safeData.wellnessReport.movement_score_status
                    },
                    secondary_presence: {
                        count: safeData.wellnessReport.secondary_presence,
                        std_dev: safeData.wellnessReport.secondary_presence_std_dev,
                        status: safeData.wellnessReport.secondary_presence_status
                    },
                    thermal_presence: {
                        count: safeData.wellnessReport.thermal_presence,
                        std_dev: safeData.wellnessReport.thermal_presence_std_dev,
                        status: safeData.wellnessReport.thermal_presence_status
                    },
                    visit_alert: {
                        count: safeData.wellnessReport.visit_alert_count,
                        std_dev: safeData.wellnessReport.visit_alert_std_dev,
                        status: safeData.wellnessReport.visit_alert_status
                    },
                    report_interval: safeData.wellnessReport.report_interval,
                    period_start: safeData.wellnessReport.period_start,
                    period_end: safeData.wellnessReport.period_end,
                    created_at: safeData.wellnessReport.created_at,
                    data_points: safeData.wellnessReport.data_points
                }, null, 2));
            } else {
                console.log("No wellness report data available");
            }

            this.safeSetState(safeData);
        })
        .catch(error => {
            console.error('Error loading reports:', error);
            // Set default values on error
            this.safeSetState({
                dailyReport: null,
                weeklyReports: [],
                wellnessReport: null
            });
        });
    }

    analyzeData() {
        this.analyzeRangeData();
        this.analyzeSleepData();
        this.analyzeVisitData();
    }

    analyzeVisitData() {
        const smoothedVisits = C.smoothData(this.state.visits);
        const visitTransitions = C.determineTransitions(smoothedVisits, 'visits');

        // Check if we need to log anything
        if (
            visitTransitions.length === 0 &&
            _.get(_.head(smoothedVisits), 'value')
        ) {
            visitTransitions.push(
                {
                    timestamp: this.state.startTime.valueOf(),
                    type: 'START',
                },
                {
                    timestamp: this.state.endTime.valueOf(),
                    type: 'END',
                }
            );
        }

        this.safeSetState({
            smoothedVisits,
            visitTransitions,
        });
    }

    analyzeSleepData() {
        const sleepTransitions = C.determineTransitions(
            this.state.sleeping,
            'sleep'
        );

        // Check if we need to log anything
        if (
            sleepTransitions.length === 0 &&
            _.get(_.head(this.state.sleeping), 'value')
        ) {
            sleepTransitions.push(
                {
                    timestamp: this.state.startTime.valueOf(),
                    type: 'START',
                },
                {
                    timestamp: this.state.endTime.valueOf(),
                    type: 'END',
                }
            );
        }

        this.safeSetState({
            smoothedSleep: this.state.sleeping,
            sleepTransitions,
        });
    }

    analyzeRangeData() {
        const smoothedRanges = C.smoothData(this.state.outOfRangeStatus);
        const rangeTransitions = C.determineTransitions(smoothedRanges, 'ranges');

        const rangeOOOTransitions = C.determineOOOTransitions(
            smoothedRanges,
            'ranges'
        );

        if (
            rangeOOOTransitions.length === 0 &&
            _.get(_.head(smoothedRanges), 'value')
        ) {
            rangeOOOTransitions.push(
                {
                    timestamp: this.state.startTime.valueOf(),
                    type: 'END',
                    dateString: moment(this.state.startTime).toString(),
                },
                {
                    timestamp: this.state.endTime.valueOf(),
                    type: 'START',
                    dateString: moment(this.state.endTime).toString(),
                }
            );
        }
        // Check if we need to log anything
        if (
            rangeTransitions.length === 0 &&
            _.get(_.head(smoothedRanges), 'value')
        ) {
            rangeTransitions.push(
                {
                    timestamp: this.state.startTime.valueOf(),
                    type: 'START',
                    dateString: moment(this.state.startTime).toString(),
                },
                {
                    timestamp: this.state.endTime.valueOf(),
                    type: 'END',
                    dateString: moment(this.state.endTime).toString(),
                }
            );
        }

        this.safeSetState({
            smoothedRanges,
            rangeTransitions,
            rangeOOOTransitions,
        });
    }

    addListeners() {
        this.safeSetState({
            movementLoading: true,
            respirationLoading: true,
            visitLoading: true,
            alertLoading: true,
            rangeLoading: true,
            sleepLoading: false,
        });

        this.loadPatientParamsOOR(this.props.match.params.patientid).then(() => {
            this.safeSetState({
                rangeLoading: false,
            });
        });

        this.loadPatientParamsMOV(this.props.match.params.patientid).then(() => {
            this.safeSetState({
                movementLoading: false,
            });
        });

        this.loadPatientParamsLR(this.props.match.params.patientid).then(() => {
            this.safeSetState({
                respirationLoading: false,
            });
        });

        this.loadPatientParamsAvgResp(this.props.match.params.patientid).then(() => {
            this.safeSetState({
                respirationLoading: false,
            });
        });

        this.loadPatientParamsAlert(this.props.match.params.patientid).then(() => {
            this.safeSetState({
                alertLoading: false,
            });
        });

        this.loadPatientPoints(this.props.match.params.patientid).then(() => {
            // console.log('loading');
        });
    }

    removeListeners() {
        //this.props.db.off();
    }

    handleTimeFrameChange(event) {
        this.removeListeners();

        const startTime = moment(Number(event.target.value));
        const isUsingCustomDateRange = event.target.innerHTML.includes(' to ');
        const {facility, section, room} = this.props.match.params;

        ReactGA.event({
            category: 'User',
            action: 'Changed Time Filter',
            label: `/${facility}/${section}/${room} : ${event.target.innerHTML}`,
        });

        this.safeSetState(
            {
                movements: this.state.movements.filter(
                    ({timestamp}) => timestamp > startTime.valueOf()
                ),
                startTime: startTime,
                endTime: isUsingCustomDateRange ? this.state.endTime : moment(),
                selectedTimeFrame: event.target.innerHTML,
                timeFrameDropdownVisible: false,
                customRangePickerVisible: false,
                isUsingCustomDateRange,
            },
            this.addListeners
        );
    }

    handleDatesChange({startDate, endDate}) {
        let error = '';
        if (startDate && endDate && endDate.diff(startDate, 'd') > 6) {
            error = 'The maximum date range is 7 days.';
        }
        return error;
    }

    handleApplyRange({startDate, endDate}) {
        this.removeListeners();
        const {startTime, endTime} = dateTimePickerDefault;
        const startDateMomment = moment(startDate);
        const endDateMoment = moment(endDate);

        const format =
            startDateMomment.format('HH:mm') === startTime &&
            endDateMoment.format('HH:mm') === endTime
                ? 'MMM DD, YYYY'
                : 'hh:mmA  MM/DD/YY';
        this.safeSetState(
            {
                startTime: startDateMomment,
                endTime: endDateMoment,
                customRangePickerVisible: false,
                timeFrameDropdownVisible: false,
                isUsingCustomDateRange: true,
                selectedTimeFrame: `${startDateMomment.format(
                    format
                )} to ${endDateMoment.format(format)}`,
            },
            this.addListeners
        );
    }

    handleClearDateRangePicker() {
        this.safeSetState({
            lowerLimit: dateTimePickerDefault.lowerLimit,
            upperLimit: dateTimePickerDefault.upperLimit,
            timeFrameDropdownVisible: false,
            customRangePickerVisible: false,
        });
    }

    reportHeaderHeight() {
        const blankSpace = 50;
        return (
            (largeFontSize + smallFontSize + mediumFontSize + blankSpace) *
            PIXELS_TO_POINTS
        );
    }

    drawHeader(pdf, width, padding) {
        const {
            facilityDetails: {facilityName},
            sectionDetails: {sectionName = 'Section Name'},
        } = this.props.auth;
        const {name: roomName = 'Default room', startTime, endTime} = this.state;

        const mediumMargin = 25 * PIXELS_TO_POINTS;
        const smallMargin = 10 * PIXELS_TO_POINTS;
        const datetimeFormat = 'hh:mmA  MMM DD, YYYY';
        const startTimeText = startTime.format(datetimeFormat);
        const endTimeText = (endTime || moment()).format(datetimeFormat);

        const communityLength =
            pdf.getStringUnitWidth(facilityName) * mediumFontSize;
        const neighborhoodLength =
            pdf.getStringUnitWidth(sectionName) * mediumFontSize;
        const startTimeLength =
            pdf.getStringUnitWidth(startTimeText) * smallFontSize;
        const endTimeLength = pdf.getStringUnitWidth(endTimeText) * smallFontSize;

        const communityYPos = padding;
        const sectionXPos = width - padding - neighborhoodLength;

        const roomLabelYPos = communityYPos + 2 * mediumMargin;
        const roomTitleYPos = roomLabelYPos + mediumMargin;

        const startTimeLabelYPos =
            roomTitleYPos + largeFontSize - 2 * smallFontSize;
        const startTimeXPos =
            width - padding - startTimeLength - endTimeLength - mediumMargin;
        const startTimeTitleYPos = startTimeLabelYPos + smallFontSize;

        pdf.setFontType('normal');
        pdf.setFontSize(mediumFontSize);
        pdf.text(
            facilityName,
            sectionXPos - communityLength - smallMargin,
            communityYPos
        );

        pdf.setFontSize(smallFontSize);
        pdf.text('Personal Data For', padding, roomLabelYPos);
        pdf.text('From', startTimeXPos, startTimeLabelYPos);
        pdf.text(
            'To',
            startTimeXPos + startTimeLength + mediumMargin,
            startTimeLabelYPos
        );

        pdf.setFontType('bold');
        pdf.text(startTimeText, startTimeXPos, startTimeTitleYPos);
        pdf.text(
            endTimeText,
            startTimeXPos + startTimeLength + mediumMargin,
            startTimeTitleYPos
        );

        pdf.setFontSize(mediumFontSize);
        pdf.text(sectionName, sectionXPos, communityYPos);

        pdf.setFontSize(largeFontSize);
        pdf.text(roomName, padding, roomTitleYPos);
    }

    printDataReport() {
        // Temp Fix
        window.print();
        // html2canvas(this.patientDataContainer.current).then((canvas) => {
        //   const imgData = canvas.toDataURL('image/png');
        //   const padding = 50;
        //
        //   const width = (canvas.width + 2 * padding) * PIXELS_TO_POINTS;
        //   const height = Math.max(
        //     (canvas.height + 2 * padding + this.reportHeaderHeight()) *
        //       PIXELS_TO_POINTS,
        //     width / 1.4142
        //   );
        //   const pdf = new jsPDF('l', 'pt', [height, width]);
        //   pdf.addImage(
        //     imgData,
        //     'PNG',
        //     padding,
        //     padding + this.reportHeaderHeight()
        //   );
        //   this.drawHeader(pdf, width, padding * PIXELS_TO_POINTS);
        //
        //   pdf.autoPrint();
        //
        //   if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //     window.navigator.msSaveOrOpenBlob(pdf.output('blob'));
        //   } else if (BrowserDetection.is_safari) {
        //     window.open(pdf.output('bloburl'));
        //   } else {
        //     openTab(pdf.output('bloburl'));
        //   }
        // });
    }

    getThresholds = (metric, wellnessReport) => {
        // Return default thresholds if wellnessReport is null/undefined
        if (!wellnessReport || !metric) {
            return {
                low: 0,
                normalLow: 0,
                average: 0,
                normalHigh: 0,
                high: 0,
                status: 'Unknown'
            };
        }

        let average = 0;
        let stdDev = 0;
        let status = 'Unknown';

        // Handle different metric naming patterns
        switch(metric) {
            case 'overall_resp':
                average = parseFloat(wellnessReport[`${metric}_average`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'sleep_total':
                // Handle time format like "10h 12m"
                const timeStr = wellnessReport[`${metric}_average`] || "0h 0m";
                const timeMatch = timeStr.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                if (timeMatch) {
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    // Convert to total minutes for calculation
                        average = hours * 60 + minutes;
                    } else {
                        average = 0;
                    }
                    // Convert std_dev from minutes to hours+minutes format
                    stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                    status = wellnessReport[`${metric}_status`] || 'Unknown';
                    break;
            case 'movement_score':
                average = parseFloat(wellnessReport[`${metric}`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'bed_exit':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'secondary_presence':
                average = parseFloat(wellnessReport[`${metric}`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'can_prevent':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'bed_exit_alert':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'resp_alert':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'visit_count':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            case 'visit_alert':
                average = parseFloat(wellnessReport[`${metric}_count`] || 0);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
                break;
            default:
                average = parseFloat(wellnessReport[`${metric}_average`] ? wellnessReport[`${metric}_average`] : wellnessReport[`${metric}_count`]);
                stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                status = wellnessReport[`${metric}_status`] || 'Unknown';
        }

        return {
            low: Math.max(0, average - (2 * stdDev)),
            normalLow: Math.max(0, average - stdDev),
            average: Math.max(0, average),
            normalHigh: average + stdDev,
            high: average + (2 * stdDev),
            status: status
        };
    };

    // New function to get thresholds for all time periods (overall, day, night)
    getTimeSpecificThresholds = (metricType, wellnessReport) => {
        // Return default thresholds if wellnessReport is null/undefined
        if (!wellnessReport || !metricType) {
            const defaultThresholds = {
                low: 0,
                normalLow: 0,
                average: 0,
                normalHigh: 0,
                high: 0,
                status: 'Unknown'
            };
            
            return {
                overall: defaultThresholds,
                day: defaultThresholds,
                night: defaultThresholds
            };
        }

        // Define metric keys based on metric type
        let overallMetric, dayMetric, nightMetric;
        
        if (metricType === 'resp') {
            overallMetric = 'overall_resp';
            dayMetric = 'day_sleep_resp';
            nightMetric = 'night_sleep_resp';
        } else if (metricType === 'sleep') {
            overallMetric = 'sleep_total';
            dayMetric = 'sleep_day';
            nightMetric = 'sleep_night';
        } else {
            // For other metrics that don't have time-specific variants
            return {
                overall: this.getThresholds(metricType, wellnessReport),
                day: this.getThresholds(metricType, wellnessReport),
                night: this.getThresholds(metricType, wellnessReport)
            };
        }

        // Process thresholds for each time period
        const processThreshold = (metric) => {
            let average = 0;
            let stdDev = 0;
            let status = 'Unknown';

            if (metric.includes('resp')) {
                // Handle respiratory rate metrics
                if (wellnessReport[metric] && typeof wellnessReport[metric] === 'object') {
                    // Handle format where metric is an object with properties
                    average = parseFloat(wellnessReport[metric].average || 0);
                    stdDev = parseFloat(wellnessReport[metric].std_dev || 0);
                    status = wellnessReport[metric].status || 'Unknown';
                } else {
                    // Handle flat format
                    average = parseFloat(wellnessReport[`${metric}_average`] || 0);
                    stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                    status = wellnessReport[`${metric}_status`] || 'Unknown';
                }
            } else if (metric.includes('sleep')) {
                // Handle sleep metrics
                if (wellnessReport[metric] && typeof wellnessReport[metric] === 'object') {
                    // Handle newer format where sleep metrics are objects
                    const timeStr = wellnessReport[metric].average || "0h 0m";
                    const timeMatch = timeStr.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                    
                    if (timeMatch) {
                        const hours = parseInt(timeMatch[1], 10);
                        const minutes = parseInt(timeMatch[2], 10);
                        average = hours * 60 + minutes;
                    }
                    
                    stdDev = parseFloat(wellnessReport[metric].std_dev || 0);
                    status = wellnessReport[metric].status || 'Unknown';
                } else {
                    // Handle older flat format
                    const timeStr = wellnessReport[`${metric}_average`] || "0h 0m";
                    const timeMatch = timeStr.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                    
                    if (timeMatch) {
                        const hours = parseInt(timeMatch[1], 10);
                        const minutes = parseInt(timeMatch[2], 10);
                        average = hours * 60 + minutes;
                    }
                    
                    stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                    status = wellnessReport[`${metric}_status`] || 'Unknown';
                }
            } else if (metric === 'movement_score') {
                if (wellnessReport[metric] && typeof wellnessReport[metric] === 'object') {
                    // Newer format where movement_score is an object
                    average = parseFloat(wellnessReport[metric].score || 0);
                    stdDev = parseFloat(wellnessReport[metric].std_dev || 0);
                    status = wellnessReport[metric].status || 'Unknown';
                } else {
                    // Older flat format
                    average = parseFloat(wellnessReport[metric] || 0);
                    stdDev = parseFloat(wellnessReport[`${metric}_std_dev`] || 0);
                    status = wellnessReport[`${metric}_status`] || 'Unknown';
                }
            } else if (metric === 'bed_exit') {
                if (wellnessReport[metric] && typeof wellnessReport[metric] === 'object') {
                    // Newer format where bed_exit is an object
                    average = parseFloat(wellnessReport[metric].count || 0);
                    stdDev = parseFloat(wellnessReport[metric].std_dev || 0);
                    status = wellnessReport[metric].status || 'Unknown';
                } else {
                    // Older flat format or mixed formats
                    average = parseFloat(wellnessReport.bed_exits?.count || wellnessReport.bed_exit_count || 0);
                    stdDev = parseFloat(wellnessReport.bed_exits?.std_dev || wellnessReport.bed_exit_std_dev || 0);
                    status = wellnessReport.bed_exits?.status || wellnessReport.bed_exit_status || 'Unknown';
                }
            }

            // Ensure valid status value
            if (!status || status === 'Unknown' || status === '') {
                status = average > 0 ? 'pending' : 'insufficient_data';
            }

            return {
                low: Math.max(0, average - (2 * stdDev)),
                normalLow: Math.max(0, average - stdDev),
                average: Math.max(0, average),
                normalHigh: average + stdDev,
                high: average + (2 * stdDev),
                status: status
            };
        };

        // Build and return the combined thresholds object
        return {
            overall: processThreshold(overallMetric),
            day: processThreshold(dayMetric),
            night: processThreshold(nightMetric)
        };
    };

    // Helper function to calculate segments from transitions
    calculatePresenceSegments(transitions) {
        if (!transitions || transitions.length === 0) return [];
        
        const segments = [];
        let currentSegment = null;
        
        transitions.forEach((transition, index) => {
            if (transition.state === 'PRESENT') {
                currentSegment = {
                    start: moment(transition.timestamp),
                    end: null
                };
            } else if (transition.state === 'ABSENT' && currentSegment) {
                currentSegment.end = moment(transition.timestamp);
                segments.push(currentSegment);
                currentSegment = null;
            }
        });

        // Handle case where last segment is still open
        if (currentSegment) {
            currentSegment.end = moment();
            segments.push(currentSegment);
        }

        return segments;
    }

    calculateAbsenceSegments(transitions) {
        if (!transitions || transitions.length === 0) {
            return [{
                start: this.state.startTime,
                end: this.state.endTime
            }];
        }
        
        const segments = [];
        let lastEnd = moment(this.state.startTime);
        
        transitions.forEach((transition) => {
            if (transition.state === 'PRESENT') {
                // Add absence segment from last end to this presence start
                if (moment(transition.timestamp).isAfter(lastEnd)) {
                    segments.push({
                        start: lastEnd,
                        end: moment(transition.timestamp)
                    });
                }
            } else if (transition.state === 'ABSENT') {
                lastEnd = moment(transition.timestamp);
            }
        });

        // Add final segment if needed
        if (lastEnd.isBefore(this.state.endTime)) {
            segments.push({
                start: lastEnd,
                end: moment(this.state.endTime)
            });
        }

        return segments;
    }

    render() {
        const {
            patient,
            startTime,
            endTime,
            movements,
            respiration,
            sleepTransitions,
            visitTransitions,
            bedExits,
            thermalPresence,
            patientGraphLoading
        } = this.state;

        if (this.state.isLoading) {
            return <Loader/>;
        }

        // Convert UTC-7 timestamps to local time
        const convertTimestamp = (timestamp) => {
            return moment(timestamp).utcOffset(-7).valueOf();
        };

        // Process data for charts
        const chartData = {
            activityChart: {
                startTime: startTime?.format(),
                endTime: endTime?.format(),
                bedExitDataPoints: this.state.rangeOOOTransitions?.filter(t => t.type === 'START').map(exit => ({
                    timestamp: convertTimestamp(exit.timestamp),
                    type: exit.type
                })) || [],
                movementDataPoints: movements?.map(move => ({
                    timestamp: convertTimestamp(move.timestamp),
                    value: parseFloat(move.amplitude)
                })) || []
            },
            respiratoryChart: {
                startTime: startTime?.format(),
                endTime: endTime?.format(),
                originalDataPoints: respiration?.map(resp => ({
                    timestamp: convertTimestamp(resp.timestamp),
                    value: parseFloat(resp.rpm)
                })).filter(point => !isNaN(point.value)) || [],
                absenceSegments: this.calculateAbsenceSegments(visitTransitions),
                presenceSegments: this.calculatePresenceSegments(visitTransitions),
                filteredDataPoints: respiration?.length || 0
            },
            presenceChart: {
                startTime: startTime?.format(),
                endTime: endTime?.format(),
                presenceDataPoints: visitTransitions?.map(visit => ({
                    timestamp: convertTimestamp(visit.timestamp),
                    value: visit.type === 'START' ? 1 : 0
                })) || [],
                sleepDataPoints: sleepTransitions?.map(sleep => ({
                    timestamp: convertTimestamp(sleep.timestamp),
                    value: sleep.type === 'START' ? 1 : 0
                })) || [],
                secondaryPresenceDataPoints: thermalPresence?.map(presence => ({
                    timestamp: convertTimestamp(presence.timestamp),
                    value: presence.presence ? 1 : 0
                })) || []
            }
        };

        // Ensure thresholds are properly defined
        // Use the same approach as in example file
        const thresholds = {};
        if (this.props.auth && this.props.auth.facilityDetails && this.props.auth.facilityDetails.thresholds) {
            Object.assign(thresholds, _.cloneDeep(this.props.auth.facilityDetails.thresholds));
        }
        // Merge with state thresholds if available
        if (this.state.thresholds) {
            _.merge(thresholds, this.state.thresholds);
        }

        const rangeTransitions = _.defaultTo(
            _.cloneDeep(this.state.rangeTransitions),
            []
        );

        if (rangeTransitions[0] && rangeTransitions[0].type === 'END') {
            rangeTransitions.unshift({
                timestamp: this.state.startTime.valueOf(),
                type: 'START',
            });
        }
        if (_.last(rangeTransitions) && _.last(rangeTransitions).type === 'START') {
            rangeTransitions.push({
                timestamp: this.state.endTime.valueOf(),
                type: 'END',
            });
        }

        const rangeOOOTransitions = _.defaultTo(
            _.cloneDeep(this.state.rangeOOOTransitions),
            []
        );

        if (rangeOOOTransitions[0] && rangeOOOTransitions[0].type === 'END') {
            rangeOOOTransitions.unshift({
                timestamp: this.state.startTime.valueOf(),
                type: 'START',
            });
        }
        if (
            _.last(rangeOOOTransitions) &&
            _.last(rangeOOOTransitions).type === 'START'
        ) {
            rangeOOOTransitions.push({
                timestamp: this.state.endTime.valueOf(),
                type: 'END',
            });
        }

        // console.log({rangeTransitions});
        // console.log({rangeOOOTransitions});

        let roomName = this.state.name || this.state.id;
        roomName = (roomName || '').replace(/([^s])\/([^s])/, '$1 / $2');

        const timeFrame = moment.duration(
            moment(this.state.endTime).diff(this.state.startTime)
        );

        let timeFrameText;
        if (timeFrame < 25 * 60 * 60 * 1000) {
            timeFrameText = `${Math.floor(
                moment.duration(timeFrame).asHours()
            )} hours`;
        } else {
            timeFrameText = `${Math.round(moment.duration(timeFrame).asDays())} days`;
        }

        let timeFramePrefix;
        if (this.state.isUsingCustomDateRange) {
            timeFramePrefix = 'Over Span Of';
        }
        let movementsForGraph = this.state.movements.filter(
            ({isSignificant}) => isSignificant === true
        );
        let respirationForGraph = this.state.respiration.filter(
            ({rpm}) => rpm !== 0
        );

        // Add data smoothing logic from example file
        if (this.state.smoothGraphData) {
            movementsForGraph = _.flatten(
                _.chunk(
                    movementsForGraph,
                    Math.ceil(movementsForGraph.length / 25)
                ).map((chunk) =>
                    _.sortBy(
                        [_.maxBy(chunk, 'amplitude'), _.minBy(chunk, 'amplitude')],
                        'timestamp'
                    )
                )
            );
            respirationForGraph = _.flatten(
                _.chunk(
                    respirationForGraph,
                    Math.ceil(respirationForGraph.length / 25)
                ).map((chunk) =>
                    _.sortBy([_.maxBy(chunk, 'rpm'), _.minBy(chunk, 'rpm')], 'timestamp')
                )
            );
        }

        movementsForGraph = movementsForGraph.map((movement) => ({
            t: movement.timestamp,
            y: movement.amplitude,
        }));

        // Remove duplicates
        movementsForGraph = _.uniqWith(movementsForGraph, _.isEqual);

        respirationForGraph = respirationForGraph.map((respiration) => ({
            t: respiration.timestamp,
            y: respiration.rpm,
        }));

        let averageMovementCount = 0;
        let lastMovementText = 'Last Move: UNKNOWN';
        let movementCount = 0;
        if (this.state.paramsMOV && this.state.paramsMOV.num !== undefined && this.state.paramsMOV.num > 0) {
            averageMovementCount = Math.round(this.state.paramsMOV.num);
            movementCount = Math.round(this.state.paramsMOV.num);
        }
        if (this.state.paramsMOV && this.state.paramsMOV.last_move) {
            lastMovementText = `Last Move: ${moment(
                this.state.paramsMOV.last_move * 1000
            ).fromNow()}`;
        }

        const bedExitsFromOOR = [];
        if (this.state.rangeOOOTransitions && this.state.rangeOOOTransitions.length > 0) {
            // Filter for just the START transitions - these represent the beginning of an absence period
            const startTransitions = this.state.rangeOOOTransitions.filter(t => t.type === 'START');
            
            // Convert each START transition to a bed exit point
            startTransitions.forEach(transition => {
                bedExitsFromOOR.push({
                    t: transition.timestamp,
                    y: 1  // Fixed value for visualization
                });
            });
            
            console.log('Bed exits derived from out-of-range transitions:', bedExitsFromOOR);
        }

        return (
            <div className='room-show'>
                <PatientSubNav
                    roomName={roomName}
                    breadcrumbTitle={_.get(this.props, 'auth.sectionDetails.sectionName')}
                    datepickerChangeHandler={this.handleApplyRange}
                    printHandler={this.printDataReport}
                />

                <div ref={this.patientDataContainer}>
                    <div className='row'>
                        <div className='col-md-12'>
                            <Card>
                                {patient ? <PatientDetails patientId={this.props.match.params.patientid}
                                                           patient={patient}/> : null}
                                <PatientAlerts
                                    auth={this.props.auth}
                                    patientId={this.props.match.params.patientid}
                                    offCanvasRef={this.alertsOffCanvas.current}
                                    datepickerChangeHandler={this.handleApplyRange}
                                    generalAlertsDataLoading={this.state.alertLoading}
                                    generalAlertsData={this.state.paramsALERT}
                                    startTime={this.state.startTime}
                                    endTime={this.state.endTime}
                                />
                            </Card>
                        </div>
                    </div>

                    <div className='row'>
                        {/* <div className='col-md-4'>
                            <WeeklyChartCard
                                title=''
                                patientId={this.props.match.params.patientid}
                                auth={this.props.auth}
                                patientSettings={this.state.SettingResp}
                                currentMovementScore={this.state.paramsMOV.status}
                            />
                        </div> */}
                        <div className='col-md-12'>
                            <Card className='graph patient-graph-card'>
                                <div className='patient-graph-card-header'>
                                    <h4>Patient Data</h4>
                                </div>
                                <hr className='hr-margin-override-request'/>
                                <div className='patient-graph-card-info-panel'>
                                    <div className='patient-data-point'>
                                        <span className='label'>Last Reported RR</span>
                                        <span>
                                            <strong className='patient-data-point-value'>
                                                {this.state.paramsLR && this.state.paramsLR.resp ?
                                                    Math.round(parseFloat(this.state.paramsLR.resp)) : '?'}
                                            </strong> RR
                                         </span>
                                    </div>
                                    <div className='patient-data-point'>
                                        <span className='label'>Average RR</span>
                                        <span>
                                            <strong className='patient-data-point-value'>
                                                {this.state.paramsAvgResp ? Math.round(parseFloat(this.state.paramsAvgResp)) : '?'}
                                            </strong> RR
                                        </span>
                                    </div>
                                    {/* <WhisperWrapper
                                        tooltip={movementTotalTooltip}
                                        props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}>
                                        <div className='patient-data-point'>
                                            <span className='label'>Total Movement</span>
                                            <span>
                                              <strong className='patient-data-point-value'>{movementCount}</strong> MOVES
                                            </span>
                                        </div>
                                    </WhisperWrapper> */}
                                    {/* <div className='patient-data-point'>
                                        <span className='label'>Movement Score</span>
                                        <PatientInfoPanelRangeDisplayText
                                            label='Movement Score'
                                            count={this.state.paramsMOV.status}
                                            lastLabel={lastMovementText}/>
                                    </div> */}
                                </div>
                                <hr/>

                                {this.state.patientGraphLoading ? (
                                    <MiniLoader/>
                                ) : (
                                    <WhisperWrapper
                                        tooltip={graphTooltip}
                                        props={{
                                            placement: "rightStart",
                                        }}>
                                        <div>
                                            <div>
                                                Smoothing: &nbsp;
                                                <RadioToggle
                                                    value={this.state.smoothGraphData}
                                                    onClick={() =>
                                                        this.setState({
                                                            smoothGraphData: !this.state.smoothGraphData,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <RespiratoryChart
                                                respirationData={respirationForGraph}
                                                visitTransitions={this.state.visitTransitions}
                                                startTime={this.state.startTime.toDate()}
                                                endTime={this.state.endTime.toDate()}
                                                SettingResp={this.state.SettingResp}
                                                rangeOOOTransitions={this.state.rangeOOOTransitions}
                                                trendsThresholds={this.state.wellnessReport ? {
                                                    respiratory: {
                                                        normalLow: this.getThresholds('overall_resp', this.state.wellnessReport).normalLow,
                                                        normalHigh: this.getThresholds('overall_resp', this.state.wellnessReport).normalHigh
                                                    }
                                                } : null}
                                            />

                                            <PresenceChart
                                                sleepData={this.state.sleepTransitions.map(t => ({
                                                    t: t.timestamp,
                                                    y: t.type === 'START' ? 1 : 0
                                                }))}
                                                presenceData={this.state.visitTransitions.map(t => ({
                                                    t: t.timestamp,
                                                    y: t.type === 'START' ? 1 : 0
                                                }))}
                                                secondaryPresenceData={this.state.thermalPresence.map(p => ({
                                                    t: p.timestamp,
                                                    y: p.presence ? 1 : 0
                                                }))}
                                                sleepTransitions={this.state.sleepTransitions}
                                                visitTransitions={this.state.visitTransitions}
                                                startTime={this.state.startTime.toDate()}
                                                endTime={this.state.endTime.toDate()}
                                            />

                                            <ActivityChart
                                                movementData={movementsForGraph}
                                                presenceData={this.state.visitTransitions.map(t => ({
                                                    t: t.timestamp,
                                                    y: t.type === 'START' ? 1 : 0
                                                }))}
                                                bedExitsData={bedExitsFromOOR}
                                                startTime={this.state.startTime.toDate()}
                                                endTime={this.state.endTime.toDate()}
                                                smoothGraphData={this.state.smoothGraphData}
                                            />

                                            <ModularTrendsTimeSplitChart
                                                title="Respiratory Rate Trends"
                                                metricLabel="Respiratory Rate (bpm)"
                                                overallData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.overall_resp : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.value !== null && item.period_start !== null)}
                                                dayData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.day_sleep_resp : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                nightData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.night_sleep_resp : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getTimeSpecificThresholds("resp", this.state.wellnessReport)}
                                                SettingResp={this.state.SettingResp}
                                            />

                                            <ModularTrendsTimeSplitChart
                                                title="Total Sleep Trends"
                                                metricLabel="Total Sleep (Hours&Minutes)"
                                                overallData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.sleep_total_average : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                dayData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.sleep_day_average : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                nightData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.sleep_night_average : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getTimeSpecificThresholds("sleep", this.state.wellnessReport)}
                                            />

                                            <ModularTrendsChartAlerts
                                                title="Patient Alert Trends"
                                                bedExitAlerts={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.bed_exit_alert_count || 0 : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                respAlerts={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.resp_alert_count || 0 : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                visitCountAlerts={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.visit_alert_count || 0 : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={{
                                                    bedExit: this.getThresholds("bed_exit_alert", this.state.wellnessReport),
                                                    respiratory: this.getThresholds("resp_alert", this.state.wellnessReport),
                                                    visitCount: this.getThresholds("visit_alert", this.state.wellnessReport)
                                                }}
                                            />

                                            <ModularTrendsChart
                                                title="Bed Exit Count Trends"
                                                metricLabel="Bed Exits"
                                                historyData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? (report.bed_exits?.count || report.bed_exit_count || 0) : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getThresholds("bed_exit", this.state.wellnessReport)}
                                            />


                                            <ModularTrendsChart
                                                title="Secondary Presence Trends"
                                                metricLabel="Secondary Presence"
                                                historyData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? (report.secondary_presence || report.secondary_presence || 0) : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getThresholds("secondary_presence", this.state.wellnessReport)}
                                            />
                                            
                                            {/* <ModularTrendsMovement
                                                title="Movement Score Trends"
                                                metricLabel="Movement Score"
                                                historyData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? report.movement_score : null,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getThresholds("movement_score", this.state.wellnessReport)}
                                            />

                                            <ModularTrendsSpecificThreshholdChart
                                                title="Body Turns Count Trends"
                                                metricLabel="Body Turns Count"
                                                historyData={(this.state.weeklyReports || []).map(report => ({
                                                    value: report ? (report.can_prevent_count || report.can_prevent || 0) : 0,
                                                    period_start: report ? report.period_start : null
                                                })).filter(item => item.period_start !== null)}
                                                thresholds={this.getThresholds("can_prevent", this.state.wellnessReport)}
                                            /> */}
                                        </div>
                                    </WhisperWrapper>
                                )}
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default RoomShow;
